import { useState } from 'react';
import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { useApolloClient } from '@apollo/client/react';
import { createSupplier, ApiError } from '../../lib/apiClient';
import type { CreateSupplierInput } from '../../lib/apiClient';
import { LoadingSpinner, RichTextEditor, isDeltaEmpty } from '../../components/shared';
import '@tylertech/forge/button';
import '@tylertech/forge/icon';

export const Route = createFileRoute('/suppliers/new')({
  component: NewSupplierPage,
});

export { NewSupplierPage };

function NewSupplierPage(): JSX.Element {
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');

  const IconElement = 'forge-icon' as React.ElementType;
  const ForgeButton = 'forge-button' as React.ElementType;

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      const input: CreateSupplierInput = {
        name: name.trim(),
        description: isDeltaEmpty(description) ? undefined : description,
        websiteUrl: websiteUrl.trim() || undefined,
      };
      await createSupplier(input);
      await apolloClient.refetchQueries({ include: ['ListSuppliers'] });
      void navigate({ to: '/suppliers' });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to create supplier');
      setIsSubmitting(false);
    }
  };

  if (isSubmitting) return <LoadingSpinner centered message="Creating supplier..." />;

  return (
    <div className="simple-entity-form-page">
      <div className="simple-entity-form-page__back-link">
        <Link to="/suppliers" className="simple-entity-form-page__back-link-inner">
          <IconElement
            name="arrow_back"
            aria-hidden="true"
            className="simple-entity-form-page__back-icon"
          />
          <span className="forge-typography--body2">Back to Suppliers</span>
        </Link>
      </div>

      <h1 className="forge-typography--headline4 simple-entity-form-page__title">Add Supplier</h1>

      {error && (
        <p role="alert" className="simple-entity-form-page__error">
          {error}
        </p>
      )}

      <form onSubmit={handleSubmit} className="simple-entity-form-page__form">
        <div>
          <label className="forge-typography--body2 simple-entity-form-page__label">
            Name <span className="simple-entity-form-page__required">*</span>
          </label>
          <input
            className="forge-text-field__input simple-entity-form-page__control"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Microsoft, Oracle, AWS"
            required
          />
        </div>
        <div>
          <label className="forge-typography--body2 simple-entity-form-page__label">
            Website URL
          </label>
          <input
            className="forge-text-field__input simple-entity-form-page__control"
            type="url"
            value={websiteUrl}
            onChange={(e) => setWebsiteUrl(e.target.value)}
            placeholder="https://example.com"
          />
        </div>
        <div>
          <label className="forge-typography--body2 simple-entity-form-page__label">
            Description
          </label>
          <RichTextEditor
            value={description}
            onChange={setDescription}
            placeholder="Notes about this supplier, contract details, etc."
          />
        </div>
        <div className="simple-entity-form-page__actions">
          <Link to="/suppliers">
            <ForgeButton type="button" variant="outlined">
              Cancel
            </ForgeButton>
          </Link>
          <ForgeButton type="submit" variant="raised" disabled={isSubmitting}>
            Add Supplier
          </ForgeButton>
        </div>
      </form>
    </div>
  );
}
